import { Link } from "react-router-dom";
import {
  BookOpen, Compass, Backpack, Shirt, HelpCircle, HandHeart, Landmark,
  Bell, FileCheck, Newspaper, Users, Download, FileText,
} from "lucide-react";
import { PREPARE_FOR_UMRAH, STAY_INFORMED, DOWNLOADS } from "@/data/content";
import usePageTitle from "@/hooks/usePageTitle";

const PREPARE_ICONS = [Compass, BookOpen, Backpack, Shirt, HelpCircle, HandHeart, Landmark];
const INFORM_ICONS = [Bell, FileCheck, Newspaper, Users];

export default function Resources() {
  usePageTitle("Pilgrim Resources · Maqam Community Initiatives");

  return (
    <div data-testid="page-resources" className="bg-white">
      <section className="relative pt-32 md:pt-40 pb-12 md:pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <span className="overline">Pilgrim Resources</span>
          <h1 className="font-serif text-5xl md:text-7xl text-[color:var(--maqam-navy)] mt-3 leading-[1.02] tracking-tight max-w-3xl">
            Everything you need to prepare with confidence.
          </h1>
          <p className="mt-6 text-[var(--maqam-muted)] max-w-2xl leading-relaxed">
            Open to everyone. Learn how to prepare for Umrah, stay informed on travel and visa
            updates, and access helpful downloads as they become available.
          </p>
        </div>
      </section>

      {/* Prepare for Umrah */}
      <section data-testid="resources-prepare" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl">
            <span className="overline">Prepare for Umrah</span>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl text-[color:var(--maqam-navy)] tracking-tight">
              Learn the journey before you live it.
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PREPARE_FOR_UMRAH.map((item, i) => {
              const Icon = PREPARE_ICONS[i % PREPARE_ICONS.length];
              return (
                <article
                  key={i}
                  data-testid={`prepare-card-${i}`}
                  className="card-elevated rounded-xl p-7"
                >
                  <span className="inline-flex w-11 h-11 rounded-full items-center justify-center" style={{ background: "rgba(200,162,74,0.16)" }}>
                    <Icon size={20} className="text-[color:var(--maqam-navy)]" />
                  </span>
                  <h3 className="mt-5 font-serif text-xl text-[color:var(--maqam-navy)] leading-tight">{item.title}</h3>
                  <p className="mt-2 text-sm text-[var(--maqam-muted)] leading-relaxed">{item.body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stay Informed */}
      <section data-testid="resources-stay-informed" className="py-16 md:py-24 bg-[var(--maqam-surface)]">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl">
            <span className="overline">Stay Informed</span>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl text-[color:var(--maqam-navy)] tracking-tight">
              Updates that matter to pilgrims.
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STAY_INFORMED.map((item, i) => {
              const Icon = INFORM_ICONS[i % INFORM_ICONS.length];
              return (
                <article
                  key={i}
                  data-testid={`inform-card-${i}`}
                  className="card-elevated rounded-xl p-6"
                >
                  <span className="inline-flex w-10 h-10 rounded-full items-center justify-center" style={{ background: "rgba(200,162,74,0.16)" }}>
                    <Icon size={18} className="text-[color:var(--maqam-navy)]" />
                  </span>
                  <h3 className="mt-4 font-serif text-lg text-[color:var(--maqam-navy)] leading-tight">{item.title}</h3>
                  <p className="mt-2 text-sm text-[var(--maqam-muted)] leading-relaxed">{item.body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Downloads */}
      <section data-testid="resources-downloads" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl">
            <span className="overline">Downloads</span>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl text-[color:var(--maqam-navy)] tracking-tight">
              Helpful resources — arriving soon.
            </h2>
            <p className="mt-4 text-[var(--maqam-muted)] leading-relaxed">
              We're preparing downloadable guides for every stage of your journey. They'll appear
              here as soon as they're ready.
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {DOWNLOADS.map((d, i) => (
              <div
                key={i}
                data-testid={`download-card-${i}`}
                aria-disabled="true"
                className="rounded-xl border border-dashed border-[var(--maqam-border)] bg-[var(--maqam-surface)] p-5 flex items-center justify-between opacity-90"
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex w-10 h-10 rounded-lg items-center justify-center bg-white border border-[var(--maqam-border)]">
                    <FileText size={18} className="text-[color:var(--maqam-navy)]" />
                  </span>
                  <div>
                    <p className="font-semibold text-[color:var(--maqam-navy)] text-sm">{d.title}</p>
                    <p className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--maqam-gold-text)] mt-0.5">Coming soon</p>
                  </div>
                </div>
                <Download size={16} className="text-[var(--maqam-muted)]" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Next step */}
      <section className="py-16 md:py-24 bg-[var(--maqam-surface)]">
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--maqam-navy)] tracking-tight">
            Have a question we haven't covered?
          </h2>
          <p className="mt-4 text-[var(--maqam-muted)]">
            Our team is happy to help you prepare for your journey.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/faq" data-testid="resources-faq-cta" className="btn-ghost rounded-full px-6 py-3 font-semibold">
              Read the FAQ
            </Link>
            <Link to="/contact" data-testid="resources-contact-cta" className="btn-gold rounded-full px-6 py-3 font-semibold">
              Contact Our Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
